import { parsePriceInput } from '@/domain/store-setup/money';
import { countryLocale } from '@/domain/store-setup/store-country';
import { ALLERGEN_BITS } from '@/interfaces/IMenuImport';
import type {
  IDraftAddonGroup,
  IDraftAddonOptionInput,
  IDraftItem,
  IDraftSize,
  IDraftSizeInput,
  IMenuDraft,
  TDraftOp,
} from '@/interfaces/IMenuImport';
import type { TAddonRuleInput } from './menu-draft';
import { cleanDraftName } from './workspace-view';

/**
 * Pure rules of the dish editor, the add-on group modal and the review queue
 * card (plan §4.5.1, §4.5.2, §4.5.4): the owner's form values, how they read
 * against the server copy of the dish, and the typed operations (§8.11) they
 * turn into. The limits mirror the API validation so a save the admin sends
 * is never refused for a value the form accepted.
 */

export const DRAFT_DESCRIPTION_MAX = 1000;
export const SIZE_LABEL_MAX = 255;
/** A dish has no sizes, or 2 to 20. */
export const SIZES_MIN = 2;
export const SIZES_MAX = 20;
export const GROUP_TITLE_MAX = 255;
export const OPTION_NAME_MAX = 255;
export const GROUP_OPTIONS_MAX = 100;
/** Typed prices: long enough for «99.999.999,99». */
export const PRICE_INPUT_MAX_LENGTH = 16;
/** Dish photos are resized in the browser to this long edge (JPEG q0.85). */
export const DISH_PHOTO_LONG_EDGE = 1600;
/**
 * The upload's file name: `/files/upload` checks the original name's
 * extension and the stored key takes it (a `.heic` or `.webp` would be refused).
 */
export const DISH_PHOTO_UPLOAD_NAME = 'photo.jpg';
/** The dynamic modal of «Agregar grupo» / «Editar». */
export const ADDON_GROUP_MODAL = 'v2-menu-addon-group';
/** Temp id of a group created by the modal (never shaped like a draft id). */
export const NEW_GROUP_TEMP_ID = 'newGroup';

// ---------------------------------------------------------------------------
// Prices typed by the owner
// ---------------------------------------------------------------------------

/**
 * A price as the owner edits it: the country's decimal separator, 0 or 2
 * decimals and no grouping («8000», «8,50»), so `parsePriceInput` reads it
 * back as the same number in every supported country.
 */
export function formatPriceInput(
  value: number | null,
  country: string
): string {
  if (value === null) return '';
  const decimals = value % 1 === 0 ? 0 : 2;
  return new Intl.NumberFormat(countryLocale(country), {
    useGrouping: false,
    minimumFractionDigits: decimals,
    maximumFractionDigits: 2,
  }).format(value);
}

const ZERO_PRICE = /^0+(?:[.,]0{1,2})?$/;

/** «Precio extra» of an add-on option: empty or zero is free (0), else a price above 0; null when unreadable. */
export function parseExtraPriceInput(
  text: string,
  country: string
): number | null {
  const trimmed = text.trim();
  if (trimmed === '' || ZERO_PRICE.test(trimmed)) return 0;
  return parsePriceInput(trimmed, country);
}

// ---------------------------------------------------------------------------
// Price or sizes
// ---------------------------------------------------------------------------

type TPricedItem = Pick<IDraftItem, 'price' | 'sizes'>;

export interface ISizeRowForm {
  /** Stable key of the row (the size id, or a local key for a new row). */
  key: string;
  label: string;
  priceText: string;
}

/** «Precio» while `rows` is empty, else «Tamaños». */
export interface IPriceSizesForm {
  priceText: string;
  rows: ISizeRowForm[];
}

export interface ISizeRowErrors {
  label: boolean;
  price: boolean;
}

export interface IPriceSizesErrors {
  /** The single price is empty or not a price above 0. */
  price: boolean;
  /** Not 2 to 20 sizes. */
  count: boolean;
  /** Only the rows with an error, by row key. */
  rows: Record<string, ISizeRowErrors>;
}

export type TPriceSizesValue =
  | { kind: 'price'; price: number | null }
  | { kind: 'sizes'; sizes: IDraftSizeInput[] };

export type TPriceSizesRead =
  | { status: 'valid'; value: TPriceSizesValue }
  | { status: 'invalid'; errors: IPriceSizesErrors };

/** The form of a dish's price or sizes. */
export function priceSizesForm(
  item: TPricedItem,
  country: string
): IPriceSizesForm {
  if (item.sizes.length === 0)
    return { priceText: formatPriceInput(item.price, country), rows: [] };
  return {
    priceText: '',
    rows: item.sizes.map((size) => ({
      key: size.id,
      label: size.label,
      priceText: formatPriceInput(size.price, country),
    })),
  };
}

function noPriceErrors(): IPriceSizesErrors {
  return { price: false, count: false, rows: {} };
}

/**
 * Reads the form against the dish: a single price above 0 (an emptied price
 * is not saved, a price that is missing now may stay missing), or 2 to 20
 * sizes, each named (≤60) with an empty (missing) or valid price.
 */
export function readPriceSizes(
  form: IPriceSizesForm,
  item: TPricedItem,
  country: string
): TPriceSizesRead {
  if (form.rows.length === 0) {
    const text = form.priceText.trim();
    const missingNow = item.price === null || item.sizes.length > 0;
    const price = text === '' ? null : parsePriceInput(text, country);
    if ((text === '' && !missingNow) || (text !== '' && price === null))
      return { status: 'invalid', errors: { ...noPriceErrors(), price: true } };
    return { status: 'valid', value: { kind: 'price', price } };
  }

  const errors = noPriceErrors();
  errors.count = form.rows.length < SIZES_MIN || form.rows.length > SIZES_MAX;
  const sizes = form.rows.map(
    (row): IDraftSizeInput => {
      const label = row.label.trim();
      const text = row.priceText.trim();
      const price = text === '' ? null : parsePriceInput(text, country);
      const rowErrors: ISizeRowErrors = {
        label: label.length === 0 || label.length > SIZE_LABEL_MAX,
        price: text !== '' && price === null,
      };
      if (rowErrors.label || rowErrors.price) errors.rows[row.key] = rowErrors;
      return { label, price };
    }
  );
  if (errors.count || Object.keys(errors.rows).length > 0)
    return { status: 'invalid', errors };
  return { status: 'valid', value: { kind: 'sizes', sizes } };
}

/** The sizes a dish has are exactly these (same order, labels and prices). */
export function sameSizes(
  current: IDraftSize[],
  next: IDraftSizeInput[]
): boolean {
  return (
    current.length === next.length &&
    current.every(
      (size, index) =>
        size.label === next[index].label && size.price === next[index].price
    )
  );
}

/**
 * The operations that give the dish this price or these sizes: one `setSizes`
 * per save; sizes back to a single price turn the sizes off first, then set
 * the price, in the same batch (the API refuses a price on a dish with sizes).
 */
export function priceSizesOps(
  item: Pick<IDraftItem, 'id' | 'price' | 'sizes'>,
  value: TPriceSizesValue
): TDraftOp[] {
  if (value.kind === 'sizes')
    return sameSizes(item.sizes, value.sizes)
      ? []
      : [{ op: 'setSizes', itemId: item.id, sizes: value.sizes }];
  if (item.sizes.length > 0) {
    const ops: TDraftOp[] = [{ op: 'setSizes', itemId: item.id, sizes: [] }];
    if (value.price !== null)
      ops.push({ op: 'updateItem', itemId: item.id, price: value.price });
    return ops;
  }
  if (value.price === null || value.price === item.price) return [];
  return [{ op: 'updateItem', itemId: item.id, price: value.price }];
}

/** The form once saved: the values as the server will store them, row keys kept. */
export function normalizePriceSizes(
  form: IPriceSizesForm,
  value: TPriceSizesValue,
  country: string
): IPriceSizesForm {
  if (value.kind === 'price')
    return { priceText: formatPriceInput(value.price, country), rows: [] };
  return {
    priceText: '',
    rows: form.rows.map((row, index) => ({
      key: row.key,
      label: value.sizes[index].label,
      priceText: formatPriceInput(value.sizes[index].price, country),
    })),
  };
}

/** The first «Agregar tamaño»: the single price becomes two sizes, the current price and an empty one. */
export function startSizes(
  form: IPriceSizesForm,
  keys: readonly [string, string]
): IPriceSizesForm {
  return {
    priceText: '',
    rows: [
      { key: keys[0], label: '', priceText: form.priceText },
      { key: keys[1], label: '', priceText: '' },
    ],
  };
}

/** One more empty size (never more than 20). */
export function addSizeRow(
  form: IPriceSizesForm,
  key: string
): IPriceSizesForm {
  if (form.rows.length === 0 || form.rows.length >= SIZES_MAX) return form;
  return { ...form, rows: [...form.rows, { key, label: '', priceText: '' }] };
}

/** «Quitar tamaño»: a single remaining size turns back into a plain price. */
export function removeSizeRow(
  form: IPriceSizesForm,
  key: string
): IPriceSizesForm {
  const rows = form.rows.filter((row) => row.key !== key);
  if (rows.length === form.rows.length) return form;
  if (rows.length <= 1)
    return { priceText: rows[0]?.priceText ?? '', rows: [] };
  return { ...form, rows };
}

export function updateSizeRow(
  form: IPriceSizesForm,
  key: string,
  patch: Partial<Pick<ISizeRowForm, 'label' | 'priceText'>>
): IPriceSizesForm {
  return {
    ...form,
    rows: form.rows.map((row) =>
      row.key === key ? { ...row, ...patch } : row
    ),
  };
}

// ---------------------------------------------------------------------------
// The dish form (name, description, price or sizes)
// ---------------------------------------------------------------------------

export type TDishField = 'name' | 'description' | 'priceSizes';

export const DISH_FIELDS: readonly TDishField[] = [
  'name',
  'description',
  'priceSizes',
];

export interface IDishForm {
  name: string;
  description: string;
  priceSizes: IPriceSizesForm;
}

export interface IDishFormErrors {
  /** Empty name. */
  name: boolean;
  priceSizes: IPriceSizesErrors | null;
}

export const NO_DISH_FORM_ERRORS: Readonly<IDishFormErrors> = Object.freeze({
  name: false,
  priceSizes: null,
});

export type TDishFormRead =
  | { status: 'valid'; ops: TDraftOp[]; form: IDishForm }
  | { status: 'invalid'; errors: IDishFormErrors };

/** The form as the server has the dish. */
export function dishForm(item: IDraftItem, country: string): IDishForm {
  return {
    name: item.name,
    description: item.description ?? '',
    priceSizes: priceSizesForm(item, country),
  };
}

/** The server copy of one field differs between two versions of the dish. */
export function dishFieldChanged(
  field: TDishField,
  before: IDraftItem,
  after: IDraftItem
): boolean {
  if (field === 'name') return before.name !== after.name;
  if (field === 'description') return before.description !== after.description;
  return before.price !== after.price || !sameSizes(before.sizes, after.sizes);
}

/**
 * Reads `fields` of the form against the dish. Valid: the operations that
 * save what changed (nothing when unchanged) and the form as it will be
 * stored. Invalid: nothing is saved, the errors say where.
 */
export function readDishForm(
  item: IDraftItem,
  form: IDishForm,
  fields: readonly TDishField[],
  country: string
): TDishFormRead {
  const errors: IDishFormErrors = { name: false, priceSizes: null };
  const normalized: IDishForm = { ...form };
  const update: { name?: string; description?: string | null } = {};
  let priceOps: TDraftOp[] = [];

  if (fields.includes('name')) {
    const name = cleanDraftName(form.name);
    if (name === null) errors.name = true;
    else {
      normalized.name = name;
      if (name !== item.name) update.name = name;
    }
  }
  if (fields.includes('description')) {
    const text = form.description.trim().slice(0, DRAFT_DESCRIPTION_MAX);
    const description = text === '' ? null : text;
    normalized.description = text;
    if (description !== item.description) update.description = description;
  }
  if (fields.includes('priceSizes')) {
    const read = readPriceSizes(form.priceSizes, item, country);
    if (read.status === 'invalid') errors.priceSizes = read.errors;
    else {
      priceOps = priceSizesOps(item, read.value);
      normalized.priceSizes = normalizePriceSizes(
        form.priceSizes,
        read.value,
        country
      );
    }
  }

  if (errors.name || errors.priceSizes !== null)
    return { status: 'invalid', errors };
  const ops: TDraftOp[] = [];
  if (update.name !== undefined || update.description !== undefined)
    ops.push({ op: 'updateItem', itemId: item.id, ...update });
  return { status: 'valid', ops: [...ops, ...priceOps], form: normalized };
}

// ---------------------------------------------------------------------------
// Add-on groups
// ---------------------------------------------------------------------------

/** «Eligen una» | «Eligen varias» | «Extras». */
export type TGroupMode = 'pickOne' | 'pickMany' | 'extras';

export const GROUP_MODES: readonly TGroupMode[] = [
  'pickOne',
  'pickMany',
  'extras',
];

export interface IOptionRowForm {
  key: string;
  name: string;
  priceText: string;
  /** An existing option's price and its text: kept as is while the text is untouched (a copied discount can be negative). */
  original: { text: string; price: number } | null;
}

export interface IAddonGroupForm {
  title: string;
  mode: TGroupMode;
  required: boolean;
  /** «Mínimo» of «Eligen varias» (0 = none). */
  min: number;
  /** «Máximo» of «Eligen varias» and «Extras»; null = «Sin límite». */
  max: number | null;
  options: IOptionRowForm[];
}

type TUpsertGroupOp = Extract<TDraftOp, { op: 'upsertAddonGroup' }>;

/** What «Guardar» sends for the group (without the id or temp id). */
export type TAddonGroupInput = Omit<
  TUpsertGroupOp,
  'op' | 'groupId' | 'tempId'
>;

export interface IOptionRowErrors {
  name: boolean;
  price: boolean;
}

export type TAddonGroupRead =
  | { status: 'valid'; group: TAddonGroupInput }
  | {
      status: 'invalid';
      /** No group name. */
      title: boolean;
      /** Minimum or maximum out of range (`menuImport.group.minMax`). */
      rules: boolean;
      /** Only the rows with an error, by row key. */
      options: Record<string, IOptionRowErrors>;
    };

export function isGroupMode(value: string): value is TGroupMode {
  return GROUP_MODES.some((mode) => mode === value);
}

export function groupMode(
  group: Pick<IDraftAddonGroup, 'kind' | 'multiple'>
): TGroupMode {
  if (group.kind === 'add') return 'extras';
  return group.multiple ? 'pickMany' : 'pickOne';
}

/** A free option shows an empty «Precio extra». */
export function optionPriceText(price: number, country: string): string {
  return price === 0 ? '' : formatPriceInput(price, country);
}

function emptyOptionRow(key: string): IOptionRowForm {
  return { key, name: '', priceText: '', original: null };
}

/** The modal's form: the group as it is, or a new «Extras» group with one empty option. */
export function addonGroupForm(
  group: IDraftAddonGroup | null,
  country: string,
  newKey: () => string
): IAddonGroupForm {
  if (!group)
    return {
      title: '',
      mode: 'extras',
      required: false,
      min: 0,
      max: null,
      options: [emptyOptionRow(newKey())],
    };
  const mode = groupMode(group);
  // «Eligen varias» with a minimum is mandatory for diners whatever the flag
  // says (the storefront reads min > 0 as required): the switch shows it.
  const required =
    mode !== 'extras' &&
    (group.required || (mode === 'pickMany' && (group.min ?? 0) >= 1));
  return {
    title: group.title,
    mode,
    required,
    min: mode === 'pickMany' ? Math.max(group.min ?? 0, required ? 1 : 0) : 0,
    max: mode === 'pickOne' ? null : group.max,
    options: group.options.map((option) => {
      const text = optionPriceText(option.price, country);
      return {
        key: option.id,
        name: option.name,
        priceText: text,
        original: { text, price: option.price },
      };
    }),
  };
}

/** The group's rule as the API stores it (the normalisations of §8.11). */
export function groupFormRules(form: IAddonGroupForm): TAddonRuleInput {
  if (form.mode === 'extras')
    return {
      kind: 'add',
      required: false,
      multiple: true,
      min: null,
      max: form.max,
    };
  if (form.mode === 'pickOne')
    return {
      kind: 'choose',
      required: form.required,
      multiple: false,
      min: null,
      max: null,
    };
  return {
    kind: 'choose',
    required: form.required,
    multiple: true,
    min: form.min >= 1 ? form.min : null,
    max: form.max,
  };
}

/** A required «Eligen varias» asks at least one choice. */
function minFloor(form: Pick<IAddonGroupForm, 'mode' | 'required'>): number {
  return form.mode === 'pickMany' && form.required ? 1 : 0;
}

export function setGroupMode(
  form: IAddonGroupForm,
  mode: TGroupMode
): IAddonGroupForm {
  if (mode === form.mode) return form;
  if (mode === 'pickOne') return { ...form, mode, min: 0, max: null };
  if (mode === 'extras') return { ...form, mode, required: false, min: 0 };
  const next = { ...form, mode };
  return { ...next, min: Math.max(next.min, minFloor(next)) };
}

/**
 * «¿Es obligatorio elegir?». In «Eligen varias» the switch and «Mínimo» say
 * the same thing (diners must pick when the minimum is 1 or more): on asks
 * at least one, off drops the minimum to 0 (review round 4, m2).
 */
export function setGroupRequired(
  form: IAddonGroupForm,
  required: boolean
): IAddonGroupForm {
  const next = { ...form, required };
  if (next.mode === 'pickMany' && !required) return { ...next, min: 0 };
  return { ...next, min: Math.max(next.min, minFloor(next)) };
}

/** «Mínimo»: in «Eligen varias» a minimum of 1 or more turns «¿Es obligatorio elegir?» on. */
export function setGroupMin(
  form: IAddonGroupForm,
  min: number
): IAddonGroupForm {
  if (form.mode !== 'pickMany') return { ...form, min };
  return { ...form, min, required: min >= 1 };
}

/** The bounds of the «Mínimo» and «Máximo» steppers. */
export interface IGroupLimitBounds {
  minLow: number;
  minHigh: number;
  maxLow: number;
  maxHigh: number;
}

export function groupLimitBounds(form: IAddonGroupForm): IGroupLimitBounds {
  const count = Math.max(form.options.length, 1);
  const minLow = minFloor(form);
  return {
    minLow,
    minHigh: Math.max(minLow, Math.min(form.max ?? count, count)),
    maxLow: Math.max(1, form.min),
    maxHigh: count,
  };
}

/** «Sin límite» off: a maximum that fits the minimum and the options. */
export function limitedMax(form: IAddonGroupForm): number {
  const count = Math.max(form.options.length, 1);
  return Math.min(Math.max(1, form.min), count);
}

export function addOptionRow(
  form: IAddonGroupForm,
  key: string
): IAddonGroupForm {
  if (form.options.length >= GROUP_OPTIONS_MAX) return form;
  return { ...form, options: [...form.options, emptyOptionRow(key)] };
}

/** «Quitar opción» (never the last one); the minimum and maximum shrink with the options. */
export function removeOptionRow(
  form: IAddonGroupForm,
  key: string
): IAddonGroupForm {
  const options = form.options.filter((option) => option.key !== key);
  if (options.length === form.options.length || options.length === 0)
    return form;
  const count = options.length;
  return {
    ...form,
    options,
    min: Math.min(form.min, count),
    max: form.max === null ? null : Math.max(1, Math.min(form.max, count)),
  };
}

export function updateOptionRow(
  form: IAddonGroupForm,
  key: string,
  patch: Partial<Pick<IOptionRowForm, 'name' | 'priceText'>>
): IAddonGroupForm {
  return {
    ...form,
    options: form.options.map((option) =>
      option.key === key ? { ...option, ...patch } : option
    ),
  };
}

/** An option's price: the stored one while its text is untouched, else the typed one (null when unreadable). */
export function optionRowPrice(
  option: IOptionRowForm,
  country: string
): number | null {
  if (option.original && option.priceText === option.original.text)
    return option.original.price;
  return parseExtraPriceInput(option.priceText, country);
}

/** The minimum and maximum the API would refuse (`repairGroupRules` issues). */
function rulesOutOfRange(rules: TAddonRuleInput, count: number): boolean {
  if (!rules.multiple) return false;
  const { min, max } = rules;
  if (min !== null && (min < 0 || min > count)) return true;
  if (max !== null && (max < 1 || max > count)) return true;
  return min !== null && max !== null && min > max;
}

/**
 * «Guardar» of the add-on group modal: a name (≤255), 1 to 100 options each
 * named (≤255) with a free or positive extra price, and a minimum and
 * maximum within the options (§8.11, `OptionsService.validateOptionBasics`).
 */
export function readAddonGroupForm(
  form: IAddonGroupForm,
  country: string
): TAddonGroupRead {
  const title = form.title.trim();
  const titleError = title.length === 0 || title.length > GROUP_TITLE_MAX;
  const optionErrors: Record<string, IOptionRowErrors> = {};
  const options = form.options.map(
    (option): IDraftAddonOptionInput => {
      const name = option.name.trim();
      const price = optionRowPrice(option, country);
      const errors: IOptionRowErrors = {
        name: name.length === 0 || name.length > OPTION_NAME_MAX,
        price: price === null,
      };
      if (errors.name || errors.price) optionErrors[option.key] = errors;
      return { name, price: price ?? 0 };
    }
  );
  const rules = groupFormRules(form);
  const countError = options.length === 0 || options.length > GROUP_OPTIONS_MAX;
  const rulesError = rulesOutOfRange(rules, options.length);
  if (
    titleError ||
    countError ||
    rulesError ||
    Object.keys(optionErrors).length > 0
  )
    return {
      status: 'invalid',
      title: titleError || countError,
      rules: rulesError,
      options: optionErrors,
    };
  return { status: 'valid', group: { title, ...rules, options } };
}

/** «Guardar»: the group upserted, and a new one linked to the dish, in one batch. */
export function groupSaveOps(
  group: Pick<IDraftAddonGroup, 'id'> | null,
  input: TAddonGroupInput,
  itemId: string
): TDraftOp[] {
  if (group) return [{ op: 'upsertAddonGroup', groupId: group.id, ...input }];
  return [
    { op: 'upsertAddonGroup', tempId: NEW_GROUP_TEMP_ID, ...input },
    { op: 'linkAddonGroup', groupId: NEW_GROUP_TEMP_ID, itemIds: [itemId] },
  ];
}

/** How many dishes use the group. */
export function groupUsage(draft: IMenuDraft, groupId: string): number {
  return Object.values(draft.items).filter((item) =>
    item.addonGroupIds.includes(groupId)
  ).length;
}

/** The dish's price, or its cheapest priced size («Este extra cuesta más que el plato»). */
export function referencePrice(item: TPricedItem): number | null {
  if (item.sizes.length === 0) return item.price;
  const prices = item.sizes
    .map((size) => size.price)
    .filter((price): price is number => price !== null);
  return prices.length > 0 ? Math.min(...prices) : null;
}

/** Options that cost more than the dish (a warning, never blocking). */
export function costlierOptionKeys(
  form: IAddonGroupForm,
  reference: number | null,
  country: string
): string[] {
  if (reference === null) return [];
  return form.options
    .filter((option) => {
      const price = optionRowPrice(option, country);
      return price !== null && price > reference;
    })
    .map((option) => option.key);
}

// ---------------------------------------------------------------------------
// Allergens
// ---------------------------------------------------------------------------

export interface IAllergenChoice {
  bit: number;
  /** The existing `allergens.*` i18n key. */
  labelKey: string;
}

/** The 14 allergens in the order of the admin's `ALLERGENS` (by bit). */
export const ALLERGEN_CHOICES: readonly IAllergenChoice[] = Object.entries(
  ALLERGEN_BITS
)
  .map(([code, bit]) => ({ bit, labelKey: `allergens.${code}` }))
  .sort((a, b) => a.bit - b.bit);

export function hasAllergen(bits: number, bit: number): boolean {
  return (bits & bit) === bit;
}

export function toggleAllergen(bits: number, bit: number): number {
  return bits ^ bit;
}

/** The `allergens.*` label keys of a bitmask, in chip order. */
export function allergenLabelKeys(bits: number): string[] {
  return ALLERGEN_CHOICES.filter((choice) => hasAllergen(bits, choice.bit)).map(
    (choice) => choice.labelKey
  );
}

// ---------------------------------------------------------------------------
// Values saved in the background
// ---------------------------------------------------------------------------

export interface IEchoSettle<T> {
  /** Sent values still to come back. */
  pending: T[];
  /** Show the server value: nothing of ours is still on its way. */
  adopt: boolean;
}

/**
 * A server value arrived for a field the owner changes with taps (category,
 * allergens, hidden, photo). One of the values sent: the ones sent before it
 * are settled, and the screen keeps the owner's latest value until it comes
 * back too. Any other value (someone else's change): adopt it.
 */
export function settleEcho<T>(
  pending: readonly T[],
  server: T
): IEchoSettle<T> {
  const index = pending.indexOf(server);
  if (index < 0) return { pending: [], adopt: true };
  const rest = pending.slice(index + 1);
  return { pending: rest, adopt: rest.length === 0 };
}

/**
 * The save of a sent value was refused: it will never come back. Saves go
 * out in order, so it is the oldest one still pending with that value.
 * Nothing else pending: the screen shows the server value again.
 */
export function dropEcho<T>(pending: readonly T[], refused: T): IEchoSettle<T> {
  const index = pending.indexOf(refused);
  const rest =
    index < 0
      ? pending.slice()
      : [...pending.slice(0, index), ...pending.slice(index + 1)];
  return { pending: rest, adopt: rest.length === 0 };
}
